import React, { useState, useEffect, useRef } from 'react';
import { Info } from 'lucide-react';
import type { Question, SectionInfo } from './types';
import QuestionCard from './QuestionCard';

interface QuestionsPanelProps {
  questions: Question[];
  onApply: (questionId: string, optionId: string, params?: Record<string, any>) => Promise<void>;
  onPreview?: (questionId: string, optionId: string, params: Record<string, any>) => Promise<{ rowsChanged: number; total: number; summaryAction: string | null } | null>;
  onNavigateToIssue: (issueId: string) => void;
  highlightQuestionId: string | null;
  availableColumns?: string[];
  sectionsByFile?: Record<string, SectionInfo[]>;
}

const SEVERITY_ORDER: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };

const QuestionsPanel: React.FC<QuestionsPanelProps> = ({
  questions,
  onApply,
  onPreview,
  onNavigateToIssue,
  highlightQuestionId,
  availableColumns,
  sectionsByFile,
}) => {
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pulsingId, setPulsingId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const appliedCount = questions.filter(q => q.applied || q.status === 'APPLIED').length;
  const totalCount = questions.length;
  const allApplied = appliedCount === totalCount && totalCount > 0;
  const progressPct = totalCount > 0 ? (appliedCount / totalCount) * 100 : 0;

  const sorted = [...questions].sort((a, b) => {
    const aApplied = a.applied || a.status === 'APPLIED' ? 1 : 0;
    const bApplied = b.applied || b.status === 'APPLIED' ? 1 : 0;
    if (aApplied !== bApplied) return aApplied - bApplied;
    const aSev = SEVERITY_ORDER[a.severity] ?? 99;
    const bSev = SEVERITY_ORDER[b.severity] ?? 99;
    if (aSev !== bSev) return aSev - bSev;
    return b.records_affected - a.records_affected;
  });

  useEffect(() => {
    if (highlightQuestionId) {
      const el = document.getElementById(`question-${highlightQuestionId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setPulsingId(highlightQuestionId);
        const timer = setTimeout(() => setPulsingId(null), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [highlightQuestionId]);

  const handleApply = async (questionId: string, optionId: string, params?: Record<string, any>) => {
    setApplyingId(questionId);
    setErrors(prev => { const next = { ...prev }; delete next[questionId]; return next; });
    try {
      await onApply(questionId, optionId, params);
    } catch (err: any) {
      setErrors(prev => ({ ...prev, [questionId]: err.message || 'Something went wrong \u2014 your data was not changed. Try again or contact support.' }));
    } finally {
      setApplyingId(null);
    }
  };

  if (totalCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-full bg-[#D1FAE5] flex items-center justify-center mb-3">
          <span className="text-lg">&#10003;</span>
        </div>
        <p className="text-sm font-semibold text-[#0F172A]">No stakeholder decisions required</p>
        <p className="text-xs text-[#94A3B8] mt-1">All issues were resolved automatically.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-4">
      <div className="bg-white border border-[#E2E8F0] rounded-xl px-5 py-4 shadow-sm">
        {allApplied ? (
          <div className="bg-[#D1FAE5] border border-[#A7F3D0] rounded-lg px-4 py-3 text-center">
            <p className="text-[14px] font-semibold text-[#065F46]">&#10003; All decisions made &mdash; your data is ready to export.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[14px] font-semibold text-[#0F172A]">Stakeholder Decisions</p>
              <p className="text-[13px] text-[#64748B]">
                <span className="font-bold text-[#0D9488]">{appliedCount}</span> of {totalCount} answered
              </p>
            </div>
            <div className="w-full h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#0D9488] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </>
        )}
      </div>

      {!allApplied && (
        <div className="flex items-start gap-2.5 bg-[#FEF3C7] border border-[#FDE68A] rounded-lg px-4 py-2.5">
          <Info className="w-4 h-4 text-[#92400E] mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-[#92400E] leading-relaxed">
            These questions require human domain knowledge &mdash; the automated cleaning process cannot resolve them without your input.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {sorted.map(question => (
          <div
            key={question.question_id}
            className={pulsingId === question.question_id ? 'animate-pulse ring-2 ring-[#0D9488] ring-offset-2 rounded-xl' : ''}
          >
            <QuestionCard
              question={question}
              onApply={handleApply}
              onPreview={onPreview}
              onNavigateToIssue={onNavigateToIssue}
              isApplying={applyingId === question.question_id}
              applyError={errors[question.question_id] || null}
              availableColumns={availableColumns}
              sectionsByFile={sectionsByFile}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuestionsPanel;
